/**
 * Tiny inline-SVG sparkline. Zero dependencies, fits in a table cell.
 *
 * Renders a simple polyline normalized to the max value in `values` plus a
 * faint baseline. If every value is 0 it renders just the baseline so the
 * cell still has a consistent height.
 *
 * Color comes from `currentColor` so it inherits the surrounding text color
 * — works in both light + dark themes and respects forced-colors mode.
 */

interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  /** Accessible description e.g. "Daily ingested posts" — required for SR users. */
  label: string;
  /** Optional max override; defaults to max of values. */
  max?: number;
}

export default function Sparkline({ values, width = 120, height = 28, label, max }: SparklineProps) {
  if (!values.length) return <span className="text-body-secondary small" aria-hidden="true">—</span>;
  const n = values.length;
  const peak = max ?? Math.max(...values);
  const safePeak = peak > 0 ? peak : 1;
  const stepX = n > 1 ? width / (n - 1) : 0;
  const padY = 2;
  const usableH = height - padY * 2;
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = padY + usableH - (v / safePeak) * usableH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      role="img"
      aria-label={`${label}. ${n} day series, peak ${peak.toLocaleString()}.`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="d-block"
    >
      <line
        x1={0}
        x2={width}
        y1={height - padY}
        y2={height - padY}
        stroke="currentColor"
        strokeOpacity={0.15}
        strokeWidth={1}
      />
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}
